define('salesInboxRouter',[
  'require',
  'exports',
  'module',
  'marionette',
  'app',
  'jquery',
  'underscore',
  'itemPagesNameMixin'
], function (require, exports, module, Marionette, App, $, _, ItemPagesNameMixin) {
  'use strict';

  var SalesInboxRouter = Marionette.AppRouter.extend({
    appRoutes: {
      'sales-inbox': 'showList',
      'sales-inbox/stats': 'showStats',
      'sales-inbox/worklist': 'showWorklist',
      // The board with one block's window open. These are the addresses the
      // window itself puts in the bar (see blockUrl in the worklist view), so
      // back, forward, reload and a pasted link all land on the same screen.
      'sales-inbox/worklist/company/:secId': 'showWorklistCompany',
      'sales-inbox/worklist/customer/:secId': 'showWorklistCustomer',
      // The board with one inbox item open on top: where a "notify me of a
      // reply" notification leads (EMAIL_REPLY_RECEIVED).
      'sales-inbox/worklist/item/:secId': 'showWorklistItem',
      // A mail notification's link: the mail's inbox item over its worklist,
      // or the mail itself when no inbox holds it.
      'sales-inbox/mail/:emailAddressSecId/:messageId': 'showMail',
      'sales-inbox/routing-rules': 'showRoutingRules',
      'sales-inbox/auto-resolve-rules': 'showAutoResolveRules',
      'sales-inbox/:secId': 'showDetail',
      // The support inbox: the same screens over the SUPPORT items (supplier
      // mail) — the list and stats are scoped with inbox=SUPPORT.
      'support-inbox': 'showSupportList',
      'support-inbox/worklist': 'showSupportWorklist',
      'support-inbox/worklist/item/:secId': 'showSupportWorklistItem',
      'support-inbox/stats': 'showSupportStats',
      'support-inbox/:secId': 'showSupportDetail'
    }
  });

  /**
   * The page a window sits on top of. Windows.setNavigation() hands the slug
   * back to App.currentUrl when the last window closes, and nothing in this
   * module used to set it — so closing an inbox window dropped the user on
   * whatever page some other router had last recorded.
   */
  function setCurrentUrl(url) {
    App.currentUrl = url;
  }

  /**
   * Back out of a window's URL and onto the board: the route fires again, and
   * the window it was showing has to go with it — it lives outside the content
   * region, so re-rendering the page leaves it floating there otherwise.
   * currentUrl is set first, so each close hands the slug back to the board.
   */
  function closeInboxWindows(then) {
    require(['windows'], function (Windows) {
      _.each((Windows.views || []).slice(), function (windowView) {
        var id = windowView.id || '';
        if (id.indexOf('worklist-customer:') === 0 || id.indexOf('sales-inbox-item:') === 0) {
          Windows.close(windowView);
        }
      });
      if (_.isFunction(then)) { then(); }
    });
  }

  var API = {
    showList: function (inbox) {
      var support = inbox === 'SUPPORT';
      require([
        'salesInboxListView',
        'contentShowController'
      ], function (ListView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl(support ? 'support-inbox' : 'sales-inbox');
          var view = new ListView({ inbox: support ? 'SUPPORT' : 'SALES' });
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', support ? ItemPagesNameMixin.SUPPORT_INBOX : ItemPagesNameMixin.SALES_INBOX);
        });
      });
    },

    showDetail: function (secId, inbox) {
      var support = inbox === 'SUPPORT';
      require([
        'salesInboxDetailView',
        'contentShowController'
      ], function (DetailView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl((support ? 'support-inbox/' : 'sales-inbox/') + secId);
          var view = new DetailView({ secId: secId });
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', support ? ItemPagesNameMixin.SUPPORT_INBOX : ItemPagesNameMixin.SALES_INBOX);
        });
      });
    },

    showSupportList: function () {
      API.showList('SUPPORT');
    },

    showSupportDetail: function (secId) {
      API.showDetail(secId, 'SUPPORT');
    },

    showSupportStats: function () {
      API.showStats('SUPPORT');
    },

    // then: optional, called once the board is shown (the router passes the
    // query string here, hence the isFunction checks).
    showSupportWorklist: function (then) {
      require([
        'supportWorklistView',
        'contentShowController'
      ], function (WorklistView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl('support-inbox/worklist');
          var view = new WorklistView();
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', ItemPagesNameMixin.SUPPORT_INBOX_WORKLIST);
          if (_.isFunction(then)) { then(); }
        });
      });
    },

    showWorklist: function (then) {
      require([
        'salesInboxWorklistView',
        'contentShowController'
      ], function (WorklistView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl('sales-inbox/worklist');
          // Arriving here from a window's URL means the user backed out of it.
          // A window to open on the board waits for that cleanup.
          closeInboxWindows(then);
          var view = new WorklistView();
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', ItemPagesNameMixin.SALES_INBOX_WORKLIST);
        });
      });
    },

    /**
     * The worklist, then the item's window on top of it — what an email row
     * of the board opens. Closing the window lands on the board.
     */
    showWorklistItem: function (secId, inbox) {
      var openItem = function () {
        require(['salesInboxDetailView'], function (DetailView) {
          DetailView.prototype.open(secId);
        });
      };
      if (inbox === 'SUPPORT') {
        API.showSupportWorklist(openItem);
      } else {
        API.showWorklist(openItem);
      }
    },

    showMail: function (emailAddressSecId, messageId) {
      var mailPopup = function () {
        App.navigate('mails/' + emailAddressSecId + '/email/' + encodeURIComponent(messageId),
          { trigger: true, replace: true });
      };
      require(['entities/sales_inbox'], function () {
        App.request('salesInbox:by-message', messageId).done(function (item) {
          if (item && item.secId) {
            App.navigate((item.inboxType === 'SUPPORT' ? 'support-inbox' : 'sales-inbox') +
              '/worklist/item/' + item.secId, { replace: true });
            API.showWorklistItem(item.secId, item.inboxType);
          } else {
            mailPopup();
          }
        }).fail(mailPopup);
      });
    },

    showSupportWorklistItem: function (secId) {
      API.showWorklistItem(secId, 'SUPPORT');
    },

    showWorklistCompany: function (secId) {
      API.showWorklistBlock({ companySecId: secId });
    },

    showWorklistCustomer: function (secId) {
      API.showWorklistBlock({ customerSecId: secId });
    },

    /**
     * The board, then the block's window on top of it. The window normally
     * opens from a click, which already holds the block; reached by URL there
     * is nothing in hand, so the block is read back from
     * GET /worklist/open — the same call the company file uses.
     */
    showWorklistBlock: function (params) {
      require([
        'salesInboxWorklistView',
        'contentShowController',
        'windows',
        'salesInboxWorklistPopupView',
        'entities/sales_inbox'
      ], function (WorklistView, ContentShowController, Windows, PopupView) {
        ContentShowController.showContent().done(function () {
          // The board is what this window sits on: closing it must land there,
          // not on the address the window itself put in the bar.
          setCurrentUrl('sales-inbox/worklist');
          var view = new WorklistView();
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', ItemPagesNameMixin.SALES_INBOX_WORKLIST);

          App.request('salesInbox:worklist-open', params).done(function (block) {
            var customer = block || {
              companySecId: params.companySecId || null,
              customerSecId: params.customerSecId || null,
              items: []
            };
            if (!customer.items) { customer.items = []; }
            var key = params.companySecId || params.customerSecId;
            var url = params.companySecId ?
              'sales-inbox/worklist/company/' + params.companySecId :
              'sales-inbox/worklist/customer/' + params.customerSecId;
            Windows.open({
              view: function () { return new PopupView({ customer: customer }); },
              navigation: function () {
                return $.Deferred().resolve(url).promise();
              },
              id: 'worklist-customer:' + key,
              className: 'worklist-customer-window',
              title: customer.companyName || customer.customerName ||
                customer.customerEmail || 'Worklist',
              width: '86%',
              height: '90%'
            });
          });
        });
      });
    },

    showStats: function (inbox) {
      var support = inbox === 'SUPPORT';
      require([
        'salesInboxStatsView',
        'contentShowController'
      ], function (StatsView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl(support ? 'support-inbox/stats' : 'sales-inbox/stats');
          var view = new StatsView({ inbox: support ? 'SUPPORT' : 'SALES' });
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', support ? ItemPagesNameMixin.SUPPORT_INBOX_STATS : ItemPagesNameMixin.SALES_INBOX_STATS);
        });
      });
    },

    showRoutingRules: function () {
      require([
        'salesInboxRoutingRulesView',
        'contentShowController'
      ], function (RoutingRulesView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl('sales-inbox/routing-rules');
          var view = new RoutingRulesView();
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', ItemPagesNameMixin.SALES_INBOX_ROUTING);
        });
      });
    },

    showAutoResolveRules: function () {
      require([
        'salesInboxAutoResolveRulesView',
        'contentShowController'
      ], function (AutoResolveRulesView, ContentShowController) {
        ContentShowController.showContent().done(function () {
          setCurrentUrl('sales-inbox/auto-resolve-rules');
          var view = new AutoResolveRulesView();
          App.regions.getRegion('main').currentView.getRegion('content').show(view);
          App.execute('set:active:nav:item', ItemPagesNameMixin.SALES_INBOX_AUTO_RESOLVE);
        });
      });
    }
  };

  App.on('before:start', function () {
    new SalesInboxRouter({ controller: API });
  });

  module.exports = SalesInboxRouter;
});

